import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

/**
 * ThemeContext - the three-state light/dark/system theme.
 *
 * =============================================================================
 * THE THREE SOURCES, AND WHICH ONE WINS
 * =============================================================================
 * A person's theme can come from three places. They are NOT peers:
 *
 *   1. the account record        durable, follows the person across devices
 *   2. localStorage              a per-browser CACHE of 1, so the pre-paint
 *                                script in index.html can apply the theme
 *                                before React exists
 *   3. the tenant's default      the storefront's starting theme, for a visitor
 *                                who has never chosen and has no account
 *
 * There is one preference per person and it lives on the account. localStorage
 * is a cache of it, not a rival: adoptRemoteTheme() overwrites the cache when
 * the server speaks, and setTheme() writes both. Treating them as equals is how
 * you end up with a person whose theme depends on which laptop they opened.
 *
 * The tenant default seeds only a visitor with nothing stored at all. It never
 * overrides a real choice, which is why it is applied behind a hasStored check
 * rather than as a fallback in the reducer.
 *
 * =============================================================================
 * THE TENANT DEFAULT AT FIRST PAINT
 * =============================================================================
 * The client is still a static Netlify SPA: netlify.toml publishes build/ with
 * an SPA catch-all to /index.html and proxies /api to Railway, and nothing in
 * the build renders this file per tenant. What changed is that a Netlify edge
 * function (client/netlify/edge-functions/meta.js) now rewrites the document on
 * its way out of the CDN and stamps data-tenant-theme onto the html element.
 *
 * So at first paint the browser knows all three inputs, and the pre-paint script
 * in index.html resolves them in the same order this file does. The first-time
 * visitor whose OS preference differed from the tenant default used to see one
 * transition when the config fetch resolved; they no longer do.
 *
 * This provider reads the same attribute when it seeds its initial state. That
 * is not belt and braces, it is required: without it React would mount holding
 * the OS value, repaint away from what the edge function had already applied,
 * and flash back when config arrived. The attribute is the only thing available
 * synchronously at mount that knows which tenant this is.
 *
 * When the edge function did not run, when it failed, or on a DeedStack
 * marketing hostname, the attribute is absent and everything below falls back to
 * exactly the previous behaviour: OS preference now, tenant default reconciled
 * when the config fetch lands.
 */

export const THEME_MODES = ['system', 'light', 'dark'];

/** Shared with the pre-paint script in public/index.html. Keep them identical. */
export const THEME_STORAGE_KEY = 'deedstack-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The meta tag colours the mobile browser chrome. Matching it avoids a light
 *  status bar sitting above a dark page. */
const META_THEME_COLOR = { light: '#1a365d', dark: '#131820' };

function isValidMode(value) {
  return THEME_MODES.includes(value);
}

/**
 * localStorage throws in a private window, when site data is blocked, and in
 * some embedded webviews. Every access is guarded and every failure is treated
 * as "no stored preference", which is the correct degradation: the theme still
 * works, it just stops being remembered.
 */
function readStoredMode() {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredMode(mode) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Not remembering the choice is acceptable. Failing the click is not.
  }
}

/**
 * The tenant's default theme as stamped on the html element by the meta edge
 * function, or null when it did not run. Read synchronously at mount so the
 * provider starts on the theme already painted rather than repainting away from
 * it. Never overrides a stored choice: every caller checks storage first.
 */
function readTenantThemeAttr() {
  try {
    const raw = document.documentElement.getAttribute('data-tenant-theme');
    return isValidMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

function systemResolved() {
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** A mode is what the person picked. A resolved theme is what the page shows. */
export function resolveTheme(mode) {
  return mode === 'system' ? systemResolved() : mode;
}

function applyResolvedTheme(resolved) {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);

  // Tells the browser to render native controls, scrollbars and form widgets in
  // the right scheme. Without it a dark page keeps white scrollbars.
  root.style.colorScheme = resolved;

  const meta = document.getElementById('theme-color');
  if (meta) {
    meta.setAttribute('content', META_THEME_COLOR[resolved] || META_THEME_COLOR.light);
  }
}

/**
 * Which account the theme belongs to, decided by the token present. The three
 * user types are three tables with three auth middlewares, so there are three
 * endpoints. Checked most privileged first: a developer with several tokens in
 * one browser should write to the session they are actually using, and the
 * master admin app is the narrowest case.
 */
function themeEndpoint() {
  try {
    const masterToken = window.localStorage.getItem('masterAdminToken');
    if (masterToken) return { path: '/master-admin/theme', token: masterToken };

    const adminToken = window.localStorage.getItem('adminToken');
    if (adminToken) return { path: '/admin/theme', token: adminToken };

    const userToken = window.localStorage.getItem('token');
    if (userToken) return { path: '/user/theme', token: userToken };
  } catch {
    return null;
  }
  return null;
}

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * @param {string} tenantDefault - the tenant's configured starting theme, from
 *   ConfigContext once the config fetch resolves. Applied only to a visitor with
 *   nothing stored.
 */
export const ThemeProvider = ({ children, tenantDefault }) => {
  // Storage first, then whatever the edge function stamped for this tenant, then
  // 'system'. Matching the pre-paint script's order is what keeps React from
  // repainting the document away from the theme already on screen.
  const initialMode = () => readStoredMode() || readTenantThemeAttr() || 'system';

  const [mode, setMode] = useState(initialMode);
  const [resolved, setResolved] = useState(() => resolveTheme(initialMode()));

  // Whether this browser holds a real choice, as opposed to having defaulted.
  // Read once at mount: after the first setTheme it is always true, and it must
  // not be recomputed from storage on every render.
  const hasStoredChoice = useRef(readStoredMode() !== null);

  /** Apply a mode to the document and to state, without persisting anywhere. */
  const applyMode = useCallback((next) => {
    const nextResolved = resolveTheme(next);
    setMode(next);
    setResolved(nextResolved);
    applyResolvedTheme(nextResolved);
  }, []);

  /**
   * The person picked a theme. Cache it, then tell the server if they are signed
   * in. The network write is deliberately fire and forget: the UI has already
   * changed, localStorage already holds it, and a failed PATCH must not reopen
   * the question or surface an error for something this small.
   */
  const setTheme = useCallback((next) => {
    if (!isValidMode(next)) return;

    applyMode(next);
    writeStoredMode(next);
    hasStoredChoice.current = true;

    const target = themeEndpoint();
    if (!target) return;

    api.patch(target.path, { theme: next }, {
      headers: { Authorization: `Bearer ${target.token}` }
    }).catch((err) => {
      console.warn('[Theme] Could not save the theme preference to the account:', err.message);
    });
  }, [applyMode]);

  /**
   * The server told us this person's stored preference. It is authoritative, so
   * it overwrites the local cache, but it does NOT get written back to the
   * server: it came from there.
   */
  const adoptRemoteTheme = useCallback((remote) => {
    if (!isValidMode(remote)) return;
    applyMode(remote);
    writeStoredMode(remote);
    hasStoredChoice.current = true;
  }, [applyMode]);

  // Follow the OS while, and only while, the person is in system mode.
  useEffect(() => {
    if (mode !== 'system') return undefined;

    let query;
    try {
      query = window.matchMedia(DARK_QUERY);
    } catch {
      return undefined;
    }

    const onChange = () => {
      const next = systemResolved();
      setResolved(next);
      applyResolvedTheme(next);
    };

    // addListener is the deprecated form, still needed for older Safari.
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, [mode]);

  // Seed a visitor who has never chosen with the tenant's default. This is the
  // reconcile half of the first-paint story described at the top of this file.
  useEffect(() => {
    if (hasStoredChoice.current) return;
    if (!isValidMode(tenantDefault)) return;
    if (tenantDefault === mode) return;
    applyMode(tenantDefault);
  }, [tenantDefault, mode, applyMode]);

  // Keep the document in step if anything desynced it, and cover the case where
  // the pre-paint script could not run at all.
  useEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  const value = {
    mode,
    resolvedTheme: resolved,
    isDark: resolved === 'dark',
    setTheme,
    adoptRemoteTheme,
    availableModes: THEME_MODES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
